import { useState } from "react";
import { DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { MultiSelect } from "~/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useDebounce } from "~/hooks/useDebounce";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { catalogDatasetsListUrl } from "~/services/catalog";
import { CaseDetailSheet } from "./CaseDetailSheet";
import { CaseFormDialog } from "./CaseFormDialog";
import { EvalCasesTab } from "./EvalCasesTab";
import { EvalRunsTab } from "./EvalRunsTab";
import { LaunchRunDialog } from "./LaunchRunDialog";
import { RunConfigSummary } from "./RunConfigSummary";
import { EVALS_PAGE_TITLE, MATRIX_RUN_COLUMNS } from "./constants/page";
import { ALL_FILTER, RUN_STATUS_FILTER_OPTIONS } from "./constants/runs";
import type { DatasetRefs } from "./primitives/chips";
import { TruncationNotice } from "./primitives/notices";
import { type CaseHealthCell, type CaseHealthRow, buildCaseHistory } from "./selectors/cases";
import { collectAgentIds, collectTags } from "./selectors/facets";
import { buildKpis } from "./selectors/kpis";
import {
  UNCONFIGURED_CONFIG_KEY,
  buildRunRows,
  buildScoreDistribution,
  buildTrend,
  groupRunsByConfig,
  runConfigKey,
} from "./selectors/runs";
import {
  sortRunsChronologically,
  useEvalCaseHistory,
  useEvalCases,
  useEvalRuns,
  useResultsForRuns,
  useRunAgentLabels,
} from "./useEvalApi";
import { EvalsTab, useEvalFilters } from "./useEvalFilters";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The evals dashboard: run history on one tab, the suite's cases on the other —
 * one tab per resource the API has. What a case checks lives in its drawer
 * rather than in a second view of the table. One filter row serves both tabs, and
 * holds its facets across a switch between them.
 */
const EvalsPage = () => {
  useDocumentTitle(EVALS_PAGE_TITLE);
  // No header breadcrumbs, matching every other nextgen list root: the nav
  // picker and the Admin sidebar's active entry already say where this is, and
  // a lone crumb repeating the page's own heading is not a trail.

  const filters = useEvalFilters();
  const [localQuery, setLocalQuery] = useState(filters.query);
  const debouncedQuery = useDebounce(localQuery, SEARCH_DEBOUNCE_MS);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  // Set when a cell is clicked rather than a row, so the drawer opens on that run.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const {
    data: casesPage,
    isLoading: casesLoading,
    mutate: refetchCases,
  } = useEvalCases(filters.tags);
  const { data: runsPage, isLoading: runsLoading, mutate: refetchRuns } = useEvalRuns(filters.tags);
  const { data: catalogDatasetList } = useFetcher<DatasetListResponse>(catalogDatasetsListUrl);
  const catalogDatasets = catalogDatasetList?.items;

  const allCases = casesPage?.items ?? [];
  const allRuns = runsPage?.items ?? [];

  // Every figure on both tabs is computed over these two lists, so a list that
  // stopped short does not shorten a table, it changes the numbers. Stated
  // rather than left to be inferred, because nothing else on the page would look
  // wrong.
  const truncations = [
    casesPage?.truncated
      ? { noun: "cases", shown: allCases.length, total: casesPage.totalCount }
      : null,
    runsPage?.truncated
      ? { noun: "runs", shown: allRuns.length, total: runsPage.totalCount }
      : null,
  ].filter((entry): entry is { noun: string; shown: number; total: number } => entry != null);

  // Name and colour together, off the one catalog read: the folder beside a
  // dataset name is coloured from the same record that supplies the name, so
  // matching the rest of the app's dataset colouring costs no extra request.
  const datasets: DatasetRefs = Object.fromEntries(
    (catalogDatasets ?? []).map((dataset) => [
      dataset.id,
      { title: dataset.title, accent_color: dataset.accent_color },
    ])
  );
  // Resolved by id rather than by browsing the agent list: the only agents this
  // page names are the ones its runs point at, and a batch read reaches agents
  // outside the reader's `usable` set — which a run can legitimately name.
  const agentLabels = useRunAgentLabels(allRuns.map((run) => run.agent_id));

  // Status and agent are applied here rather than server-side — the run list
  // route filters only by tag — and the window is taken after filtering so a
  // filter yields a full dozen columns rather than a dozen candidates minus the
  // misses.
  // Config options are drawn from the whole run set, like the agent list is, so
  // they do not shrink as the other facets narrow the runs.
  const { groups: configGroups, unconfiguredCount: unconfiguredConfigCount } =
    groupRunsByConfig(allRuns);
  const filteredRuns = allRuns.filter(
    (run) =>
      (filters.status === ALL_FILTER || run.status === filters.status) &&
      (filters.agents.length === 0 ||
        (run.agent_id != null && filters.agents.includes(run.agent_id))) &&
      (filters.config === ALL_FILTER || runConfigKey(run) === filters.config)
  );
  // The picked config, for the summary under the filter bar. Null for "All
  // configs" and for "Not recorded" — an unconfigured group has nothing to show.
  const selectedConfigGroup = configGroups.find((group) => group.key === filters.config) ?? null;
  const windowRuns = sortRunsChronologically(filteredRuns).slice(-MATRIX_RUN_COLUMNS);

  const { data: resultsBundle, isLoading: resultsLoading } = useResultsForRuns(
    windowRuns.map((run) => run.id)
  );
  const resultsByRun = resultsBundle?.resultsByRun ?? {};

  // The cases grid reads this instead of the per-run join: every case gets its
  // own newest runs, so a row is a dense progression rather than a fixed set of
  // columns most of which the case was never selected by. The run facets go to
  // the server with it so the window is taken after they apply.
  const { data: history, isLoading: historyLoading } = useEvalCaseHistory({
    tags: filters.tags,
    agentIds: filters.agents,
    status: filters.status === ALL_FILTER ? undefined : filters.status,
    runsPerCase: MATRIX_RUN_COLUMNS,
  });

  // The dataset facet narrows cases, so it belongs to the suite before the
  // search does: a KPI counting "cases in suite" should follow the facet a
  // reviewer set, and only the free-text box is a view-level narrowing.
  const scopedCases =
    filters.datasets.length === 0
      ? allCases
      : allCases.filter((evalCase) =>
          evalCase.dataset_ids.some((id) => filters.datasets.includes(id))
        );

  // Each case against its own history. The runs tab's trend, distribution and
  // table still read `resultsByRun`, because those are properties of whole runs
  // and a per-case window holds only a slice of each one.
  const suiteHistory = buildCaseHistory(scopedCases, history?.results ?? [], history?.runs ?? []);

  // The search narrows the table only. KPIs read `suiteHealth`, because a
  // reviewer typing into the search box is filtering a view, not redefining how
  // many cases the suite has or how many of them are flaky.
  const search = debouncedQuery.trim().toLowerCase();
  const health = search
    ? suiteHistory.filter(
        (row) =>
          row.evalCase.name.toLowerCase().includes(search) ||
          (row.evalCase.description ?? "").toLowerCase().includes(search)
      )
    : suiteHistory;

  const kpis = buildKpis(scopedCases, windowRuns, resultsByRun, suiteHistory);
  const trend = buildTrend(windowRuns, resultsByRun);
  const distribution = buildScoreDistribution(
    kpis.latestRun ? (resultsByRun[kpis.latestRun.id] ?? []) : []
  );
  const runRows = buildRunRows(filteredRuns, resultsByRun);
  const availableTags = collectTags(allCases, allRuns);
  const datasetOptions = collectDatasetOptions(allCases, datasets);
  const agentOptions = collectAgentIds(allRuns).map((agentId) => ({
    value: agentId,
    label: agentLabels.agentLabel(agentId) ?? agentId,
  }));

  const isLoading = casesLoading || runsLoading || resultsLoading || historyLoading;
  // The run list arrives before the results do, so for a moment every run's row
  // is on screen with its outcome columns still unknown. Not the same condition
  // as `isLoading`, which also covers having no rows to show at all.
  const resultsPending = windowRuns.length > 0 && resultsBundle == null;

  const handleQueryChange = (value: string) => {
    setLocalQuery(value);
    filters.setQuery(value);
  };

  const handleSelectCase = (caseId: string) => {
    setSelectedCaseId(caseId);
    setSelectedRunId(null);
  };

  const handleSelectCell = (row: CaseHealthRow, cell: CaseHealthCell) => {
    setSelectedCaseId(row.evalCase.id);
    setSelectedRunId(cell.runId);
  };

  const handleCloseCase = () => {
    setSelectedCaseId(null);
    setSelectedRunId(null);
  };

  return (
    <>
      {/* `wide` rather than the standard admin column: the configuration
          columns measure ~1270px before anything truncates. */}
      <AdminPage
        title={EVALS_PAGE_TITLE}
        /* The two nouns the tabs and columns are named after; nothing else
           on the page defines them. */
        description="A case tests one query; a run is an evaluation of one or more cases."
        width="wide"
      >
        <div className="flex flex-col gap-4">
          <TabBar tab={filters.tab} onSelect={filters.setTab} />

          {/* One filter row for both tabs, because every control in it reaches both
            of them. The two tabs are two projections of one slice — the cases
            grid's columns *are* runs, and the runs tab's KPIs are computed over
            cases — so a run facet legitimately moves the grid's columns and a
            case facet legitimately moves the KPIs. Splitting the row per tab
            would have to duplicate almost all of it.

            The grouping says which axis each facet acts on, which is the part a
            reviewer cannot otherwise infer: tags stands alone because it is the
            only one the API applies to both resources, filtering the case list
            and the run list server-side. */}
          <div className="flex flex-wrap items-center gap-3">
            <MultiSelect
              options={availableTags.map((tag) => ({ value: tag, label: tag }))}
              value={filters.tags}
              onValueChange={filters.setTags}
              placeholder="All tags"
              className="w-[170px]"
            />

            <FacetGroup label="Cases">
              <MultiSelect
                options={datasetOptions}
                value={filters.datasets}
                onValueChange={filters.setDatasets}
                placeholder="All datasets"
                className="w-[170px]"
              />
            </FacetGroup>

            <FacetGroup label="Runs">
              <MultiSelect
                options={agentOptions}
                value={filters.agents}
                onValueChange={filters.setAgents}
                placeholder="All agents"
                className="w-[170px]"
              />

              <Select value={filters.status} onValueChange={filters.setStatus}>
                <SelectTrigger className="w-[150px]" aria-label="Filter runs by status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RUN_STATUS_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Runs are comparable only within one judge configuration, so
                  this isolates one. Shown once there is a choice to make, or
                  whenever a config filter is already set, so it stays clearable. */}
              {(configGroups.length + (unconfiguredConfigCount > 0 ? 1 : 0) > 1 ||
                filters.config !== ALL_FILTER) && (
                <Select value={filters.config} onValueChange={filters.setConfig}>
                  <SelectTrigger className="w-[180px]" aria-label="Filter runs by judging config">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER}>All judging configs</SelectItem>
                    {configGroups.map((group) => (
                      <SelectItem key={group.key} value={group.key}>
                        Judging config {group.index}
                      </SelectItem>
                    ))}
                    {unconfiguredConfigCount > 0 && (
                      <SelectItem value={UNCONFIGURED_CONFIG_KEY}>Not recorded</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </FacetGroup>

            {/* No counts here. What a filter left on screen belongs next to the
              thing it counts — the facets act on two different axes, so one
              summary in the toolbar could only ever be read against whichever
              table happens to be open. */}
            {filters.hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setLocalQuery("");
                  filters.clearFilters();
                }}
              >
                <X className="size-4" aria-hidden />
                Clear
              </Button>
            )}
          </div>

          {/* What a picked "Config N" actually is: the label alone is opaque, so
              the judge models and any rubric overrides it stands for are spelled
              out under the filters while it is selected. Only on the runs view —
              the config facet does not act on cases. */}
          {filters.tab === EvalsTab.RUNS && selectedConfigGroup && (
            <RunConfigSummary
              key={selectedConfigGroup.key}
              label={`Judging config ${selectedConfigGroup.index}`}
              judgeConfig={selectedConfigGroup.judgeConfig}
            />
          )}

          <TruncationNotice truncations={truncations} />

          {filters.tab === EvalsTab.RUNS ? (
            <EvalRunsTab
              kpis={kpis}
              trend={trend}
              distribution={distribution}
              rows={runRows}
              agentLabels={agentLabels}
              totalRunCount={allRuns.length}
              isLoading={isLoading}
              resultsPending={resultsPending}
              unreadableRunCount={resultsBundle?.unavailableRunIds.length ?? 0}
              failedRunCount={resultsBundle?.failedRunIds.length ?? 0}
              truncatedRunCount={resultsBundle?.truncatedRunIds.length ?? 0}
              onLaunchRun={() => setLaunchOpen(true)}
              onRunDeleted={() => void refetchRuns()}
            />
          ) : (
            <EvalCasesTab
              rows={health}
              runsPerCase={MATRIX_RUN_COLUMNS}
              datasets={datasets}
              totalCaseCount={allCases.length}
              query={localQuery}
              onQueryChange={handleQueryChange}
              hiddenRunCount={history?.hidden_run_count ?? 0}
              isLoading={isLoading}
              onSelectCase={handleSelectCase}
              onSelectCell={handleSelectCell}
              onNewCase={() => setNewCaseOpen(true)}
              onDeleted={() => void refetchCases()}
            />
          )}
        </div>
      </AdminPage>

      {/* Mounted only while open, so the form resets between launches and reads
          the page's current tag filter rather than the one it loaded with. */}
      {launchOpen && (
        <LaunchRunDialog
          onOpenChange={setLaunchOpen}
          cases={allCases}
          initialTags={filters.tags}
          onLaunched={() => void refetchRuns()}
        />
      )}

      <CaseFormDialog
        open={newCaseOpen}
        onOpenChange={setNewCaseOpen}
        datasets={(catalogDatasets ?? []).map((dataset) => ({
          id: dataset.id,
          title: dataset.title,
        }))}
        availableTags={availableTags}
        onCreated={() => void refetchCases()}
      />

      <CaseDetailSheet
        open={selectedCaseId != null}
        onOpenChange={(open) => !open && handleCloseCase()}
        caseId={selectedCaseId}
        history={suiteHistory.find((row) => row.evalCase.id === selectedCaseId) ?? null}
        initialRunId={selectedRunId}
        datasets={datasets}
        availableTags={availableTags}
        onSaved={() => void refetchCases()}
      />
    </>
  );
};

/**
 * Dataset facet options, drawn from the cases rather than the whole catalog, which
 * runs to hundreds. A dataset with no catalog entry keeps a truncated id as its
 * label — a case can outlive its dataset, and dropping it would hide those cases.
 */
function collectDatasetOptions(
  cases: { dataset_ids: string[] }[],
  datasets: DatasetRefs
): { value: string; label: string }[] {
  const ids = [...new Set(cases.flatMap((evalCase) => evalCase.dataset_ids))];
  return ids
    .map((id) => ({
      value: id,
      label: datasets[id]?.title ?? `${id.slice(0, 8)}…`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/**
 * A run of facets under the resource they narrow. Both tabs read every facet, so
 * the label says which axis a control moves, not where it works: on the cases tab
 * the dataset facet takes rows out of the grid and the run facets take columns.
 */
const FacetGroup = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center gap-3 border-l border-border pl-3">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </div>
);

const TABS = [
  { value: EvalsTab.RUNS, label: "Runs" },
  { value: EvalsTab.CASES, label: "Cases" },
] as const;

/**
 * The tab strip. Deliberately not Radix `Tabs`: the selected tab lives in the
 * URL, so the panels are separate renders rather than mounted siblings, and a
 * `Tabs` root would own state the URL already owns.
 */
const TabBar = ({ tab, onSelect }: { tab: EvalsTab; onSelect: (tab: EvalsTab) => void }) => (
  <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Evals views">
    {TABS.map((option) => (
      <button
        key={option.value}
        type="button"
        role="tab"
        aria-selected={tab === option.value}
        onClick={() => onSelect(option.value)}
        className={cn(
          "-mb-px cursor-pointer border-b-2 px-3 py-2 text-sm font-medium transition-colors",
          tab === option.value
            ? "border-primary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground"
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export default EvalsPage;
